import type { PropsWithChildren } from "react";

import {
  Avatar,
  GRAVATAR_KEY,
  cn,
  getIsActivePath,
  usePreloadIntent,
} from "@stacklet/ui";
import { Link, useLocation } from "react-router";

import { paths } from "app/constants/paths";
import { useUserContext } from "app/contexts/UserContext";
import { useSettingsBreadcrumbs } from "app/hooks/routing/useSettingsBreadcrumbs";

import type { BreadCrumbElement } from "app/components/BreadCrumbsHeader";

const PROFILE_SETTINGS_PATH = paths.settingsProfile;

/**
 * Larger "hero" nav entry linking to the profile page. Reads from the
 * already-fetched app-wide UserContext rather than its own query, so it
 * doesn't add an extra network round trip to every settings page.
 */
function ProfileNavItem() {
  const { pathname } = useLocation();
  const { currentUser } = useUserContext();
  const isActive = getIsActivePath(pathname, PROFILE_SETTINGS_PATH);
  // "gravatar" (or no selection yet) falls back to Avatar's default photo-overlay behavior.
  const colorKey =
    currentUser.avatarSelection && currentUser.avatarSelection !== GRAVATAR_KEY
      ? currentUser.avatarSelection
      : undefined;

  return (
    <Link
      aria-current={isActive ? "page" : undefined}
      className={cn(
        "-mr-md flex items-center gap-sm border-r-2 border-transparent px-xs py-sm pr-md no-underline outline-none transition-colors hover:text-text-brand",
        isActive ? "border-brand-primary bg-background-secondary" : undefined,
      )}
      to={PROFILE_SETTINGS_PATH}
    >
      <Avatar
        colorKey={colorKey}
        email={currentUser.email ?? ""}
        name={currentUser.displayName ?? ""}
        size={36}
      />
      <div className="flex min-w-0 flex-col text-left">
        <span className="truncate text-label-medium-strong text-text-primary">
          {currentUser.displayName}
        </span>
        <span className="truncate text-label-small text-text-secondary">
          Profile Settings
        </span>
      </div>
    </Link>
  );
}

/**
 * Vertical tab rail listing the consolidated settings views. The tab list is
 * sourced from `useSettingsBreadcrumbs` so the rail stays in sync with the
 * breadcrumb header and inherits its feature-flag gating.
 */
type SettingsTab = NonNullable<BreadCrumbElement["tabs"]>[number];

/** Group tabs by their `section` heading, preserving first-appearance order. */
function groupTabsBySection(
  tabs: SettingsTab[],
): { section?: string; tabs: SettingsTab[] }[] {
  const groups: { section?: string; tabs: SettingsTab[] }[] = [];

  for (const tab of tabs) {
    const group = groups.find((g) => g.section === tab.section);
    if (group) {
      group.tabs.push(tab);
    } else {
      groups.push({ section: tab.section, tabs: [tab] });
    }
  }

  return groups;
}

interface SettingsTabLinkProps {
  /** The tab this link opens. */
  tab: SettingsTab;
  /** Whether the tab's section is the one on screen, including its subpages. */
  isActive: boolean;
  /** Whether the tab's own page is the one on screen. */
  isCurrent: boolean;
}

/** One link in the rail, preloading its page's queries on hover intent. */
function SettingsTabLink({ tab, isActive, isCurrent }: SettingsTabLinkProps) {
  // The tab's own page preloads nothing: its data is already on screen, and
  // the hover-preload fetch policy would put a pointless request on the wire.
  // Judged by the link's own target rather than `isActive`, which a prefix
  // match widens to the whole section — a link back to a section's list from
  // one of its detail pages still preloads.
  const preloadHandlers = usePreloadIntent(
    isCurrent ? undefined : tab.onPreload,
  );
  const TabIcon = tab.icon;

  return (
    <Link
      {...preloadHandlers}
      aria-current={isActive ? "page" : undefined}
      className={cn(
        "flex items-center gap-xs rounded-sm px-xs py-xxs text-left text-body-small text-text-primary no-underline outline-none transition-colors hover:text-text-brand",
        isActive
          ? "bg-background-brand text-text-primary-inverse hover:text-text-primary-inverse"
          : undefined,
      )}
      to={tab.target}
    >
      {TabIcon ? <TabIcon className="size-3.5 shrink-0" /> : null}
      {tab.label}
    </Link>
  );
}

function SettingsTabRail() {
  const { pathname } = useLocation();
  const breadcrumbs = useSettingsBreadcrumbs() as BreadCrumbElement[];
  const tabs = breadcrumbs.find((crumb) => crumb.tabs)?.tabs ?? [];
  const sections = groupTabsBySection(tabs);

  return (
    <nav
      aria-label="Settings sections"
      className="flex w-[14.25rem] shrink-0 flex-col gap-lg border-r border-outline-decorative pr-md"
    >
      <ProfileNavItem />
      {sections.map((group) => (
        <div className="flex flex-col gap-xs" key={group.section ?? "default"}>
          {group.section ? (
            <div className="px-xs text-left text-label-extra-small-strong capitalize text-text-secondary">
              {group.section}
            </div>
          ) : null}
          {group.tabs.map((tab) => (
            <SettingsTabLink
              isActive={getIsActivePath(pathname, tab.target)}
              isCurrent={pathname === tab.target}
              key={tab.target}
              tab={tab}
            />
          ))}
        </div>
      ))}
    </nav>
  );
}

/**
 * Layout wrapper for a top-level settings view. Renders the vertical tab rail and
 * the page content together.
 */
export function SettingsTabsLayout({ children }: PropsWithChildren) {
  return (
    <div className="flex gap-2xl">
      <SettingsTabRail />
      <div className="grow">{children}</div>
    </div>
  );
}
